/**
 * Buy now, pay later — toggle provider details on icon click.
 * Icons stay visible; click shows that provider's messaging, click again hides it.
 * Also normalizes Afterpay shadow styles to brand grey.
 */
(() => {
  const ARROW_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

  /**
   * Brand grey from Theme settings (Color Grey → --color-brand-gray).
   * @returns {string}
   */
  function getBrandGrey() {
    return (
      getComputedStyle(document.documentElement).getPropertyValue('--color-brand-gray').trim() ||
      '#5b6770'
    );
  }

  /**
   * @param {HTMLElement} placement
   */
  function styleAfterpayPlacement(placement) {
    const brandGrey = getBrandGrey();
    const themeCss = `
      :host {
        color: ${brandGrey} !important;
      }
      .afterpay-paragraph,
      .afterpay-main-text,
      .afterpay-text2,
      .afterpay-text3 {
        color: ${brandGrey} !important;
      }
      .afterpay-logo {
        display: inline-block !important;
        vertical-align: middle;
      }
      
      .afterpay-logo-badge-lockup path,
      .logo-cashappafterpay path,
      .mono-light-jewel-afterpay path,
      .mono-dark-jewel-afterpay path {
        fill: ${brandGrey} !important;
      }
      .afterpay-info-icon,
      .afterpay-info-icon path,
      .afterpay-info-icon circle,
      .afterpay-info-icon svg,
      .afterpay-info-icon svg path {
        stroke: ${brandGrey} !important;
        fill: ${brandGrey} !important;
        color: ${brandGrey} !important;
      }
    `;

    const apply = () => {
      const shadow = placement.shadowRoot;
      if (!shadow) return false;

      // Afterpay recreates its <style> node; keep ours pinned after it.
      let styleEl = shadow.querySelector('style[data-bnpl-theme]');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.setAttribute('data-bnpl-theme', 'true');
        shadow.appendChild(styleEl);
      } else if (styleEl.nextSibling) {
        shadow.appendChild(styleEl);
      }

      if (styleEl.textContent !== themeCss) {
        styleEl.textContent = themeCss;
      }

      shadow.querySelectorAll('.afterpay-logo-badge-background').forEach((node) => {
        node.setAttribute('fill', 'none');
      });

      shadow.querySelectorAll('.afterpay-logo-badge-lockup path').forEach((node) => {
        node.setAttribute('fill', brandGrey);
      });

      return true;
    };

    const start = () => {
      apply();
      let attempts = 0;
      const timer = window.setInterval(() => {
        apply();
        attempts += 1;
        if (attempts >= 24) window.clearInterval(timer);
      }, 250);
    };

    start();

    const observer = new MutationObserver(() => apply());
    if (placement.shadowRoot) {
      observer.observe(placement.shadowRoot, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 10000);
    }
  }

  class BuyNowPayLater extends HTMLElement {
    /** @type {HTMLElement[]} */
    icons = [];
    /** @type {HTMLElement[]} */
    panels = [];
    /** @type {HTMLElement | null} */
    wrapper = null;
    /** @type {string | null} */
    activeTarget = null;

    connectedCallback() {
      this.icons = Array.from(this.querySelectorAll('.js-bnpl-icon')).filter(
        (el) => el instanceof HTMLElement
      );
      this.panels = Array.from(this.querySelectorAll('.js-bnpl-wrapper')).filter(
        (el) => el instanceof HTMLElement
      );
      const wrapper = this.querySelector('.buynow-paylater__wrapper');
      this.wrapper = wrapper instanceof HTMLElement ? wrapper : null;
      this.activeTarget = null;
      this.bindEvents();
      this.hideDetails();
      this.styleAfterpay();
    }

    styleAfterpay() {
      const placement = this.querySelector('afterpay-placement');
      if (!(placement instanceof HTMLElement)) return;
      styleAfterpayPlacement(placement);
      document.addEventListener('Afterpay.ready', () => styleAfterpayPlacement(placement), {
        once: true,
      });
    }

    bindEvents() {
      this.icons.forEach((icon, index) => {
        icon.addEventListener('click', () => {
          const target = icon.getAttribute('data-bnpl-target');
          if (!target) return;
          this.togglePanel(target);
        });

        icon.addEventListener('keydown', (event) => {
          if (!ARROW_KEYS.has(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            nextIndex = (index + 1) % this.icons.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            nextIndex = (index - 1 + this.icons.length) % this.icons.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = this.icons.length - 1;
          }

          const nextIcon = this.icons[nextIndex];
          if (!nextIcon) return;
          nextIcon.focus();
        });
      });
    }

    /**
     * Click active icon again to collapse; otherwise show that provider.
     * @param {string} target
     */
    togglePanel(target) {
      if (this.activeTarget === target) {
        this.hideDetails();
        return;
      }
      this.showPanel(target);
    }

    hideDetails() {
      this.activeTarget = null;

      this.panels.forEach((panel) => {
        panel.setAttribute('hidden', 'true');
      });

      if (this.wrapper) {
        this.wrapper.setAttribute('hidden', 'true');
        this.wrapper.setAttribute('aria-hidden', 'true');
      }

      this.icons.forEach((icon) => {
        icon.setAttribute('aria-pressed', 'false');
        icon.setAttribute('aria-expanded', 'false');
        icon.removeAttribute('tabindex');
      });
    }

    /**
     * @param {string} target
     */
    showPanel(target) {
      this.activeTarget = target;

      if (this.wrapper) {
        this.wrapper.removeAttribute('hidden');
        this.wrapper.setAttribute('aria-hidden', 'false');
      }

      this.panels.forEach((panel) => {
        const panelId = panel.getAttribute('data-bnpl-panel');
        if (panelId === target) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', 'true');
        }
      });

      this.icons.forEach((icon) => {
        const isActive = icon.getAttribute('data-bnpl-target') === target;
        icon.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        icon.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('buynow-paylater')) {
    customElements.define('buynow-paylater', BuyNowPayLater);
  }
})();
